import { useEffect } from "react";
import useQuestionInsights from "admin/hooks/useQuestionInsights";
import { QUESTION_INSIGHTS_PAGE_TITLE } from "admin/types/constants/questionInsights";
import type { ClassificationSummary } from "admin/types/interfaces/questionInsights";
import { BarChart3, Layers, Loader2, MessageCircleQuestion, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { StatsCard } from "~/components/ui/stats-card";
import useAppHeader from "~/contexts/header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { trackEvent } from "~/utils/monitoring";
import { DistributionBarChart, VolumeLineChart } from "./charts";

const QuestionInsights = ({ maxContentWidth }: { maxContentWidth?: number }) => {
  useDocumentTitle(QUESTION_INSIGHTS_PAGE_TITLE);
  useAppHeader({ title: QUESTION_INSIGHTS_PAGE_TITLE });

  useEffect(() => {
    trackEvent("admin_question_insights_viewed");
  }, []);

  const { summary, isLoading, error } = useQuestionInsights();
  const hasSummary = !!summary && !error;

  return (
    <div className="relative min-h-full bg-muted">
      <div
        className="flex flex-col space-y-6 p-6"
        style={maxContentWidth ? { maxWidth: maxContentWidth, margin: "0 auto" } : undefined}
      >
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            {QUESTION_INSIGHTS_PAGE_TITLE}
          </h1>
          <p className="text-muted-foreground text-lg">
            What kind of questions are users asking AI agents over the last six months
          </p>
        </div>

        {isLoading ? (
          <SummaryLoading />
        ) : !hasSummary ? (
          <SummaryUnavailable />
        ) : (
          <>
            <HeadlineCards summary={summary} />
            {summary.total === 0 ? <EmptyWindow /> : <ChartsGrid summary={summary} />}
          </>
        )}
      </div>
    </div>
  );
};

const HeadlineCards = ({ summary }: { summary: ClassificationSummary }) => {
  const topCategory = summary.byCategory[0] ? humanizeLabel(summary.byCategory[0].category) : "—";

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatsCard
        title={summary.total.toLocaleString()}
        description="Classified questions (6 mo)"
        icon={MessageCircleQuestion}
      />
      <StatsCard
        title={summary.byCategory.length}
        description="Distinct categories"
        icon={Layers}
      />
      <StatsCard title={topCategory} description="Top category" icon={Sparkles} />
      <StatsCard title={summary.byApp.length} description="Apps" icon={BarChart3} />
    </div>
  );
};

const ChartsGrid = ({ summary }: { summary: ClassificationSummary }) => (
  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <ChartCard title="Questions by Category">
      <DistributionBarChart
        data={summary.byCategory.map((c) => ({
          label: humanizeLabel(c.category),
          count: c.count,
        }))}
      />
    </ChartCard>
    <ChartCard title="Questions by Subcategory">
      <DistributionBarChart data={summary.bySubcategory.slice(0, 10)} />
    </ChartCard>
    <ChartCard title="Question Complexity">
      <DistributionBarChart
        data={summary.byComplexity.map((c) => ({
          label: humanizeLabel(c.label),
          count: c.count,
        }))}
      />
    </ChartCard>
    <ChartCard title="Classifier Confidence">
      <DistributionBarChart
        data={summary.byConfidence.map((b) => ({
          label: `${b.lower.toFixed(1)}–${b.upper.toFixed(1)}`,
          count: b.count,
        }))}
      />
    </ChartCard>
    <div className="lg:col-span-2">
      <ChartCard title="Volume Over Time">
        <VolumeLineChart data={summary.volumeByDay} />
      </ChartCard>
    </div>
  </div>
);

const ChartCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card className="bg-background shadow-none">
    <CardHeader className="pb-2">
      <CardTitle className="text-base font-semibold">{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const SummaryLoading = () => (
  <div className="flex items-center justify-center py-12">
    <div className="flex items-center gap-3 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span>Loading insights…</span>
    </div>
  </div>
);

const SummaryUnavailable = () => (
  <Card className="border-dashed">
    <CardContent className="py-8 text-center text-sm text-muted-foreground">
      Insights are not available right now. Try again shortly.
    </CardContent>
  </Card>
);

const EmptyWindow = () => (
  <Card className="border-dashed">
    <CardContent className="py-8 text-center text-sm text-muted-foreground">
      No classified questions in the last six months yet. Insights will appear here as users
      interact with the assistants.
    </CardContent>
  </Card>
);

const humanizeLabel = (value: string): string =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");

export default QuestionInsights;
